import type { GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback, useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types";
import type {
  CancellationToken,
  ReportField,
  RunTabularReportRequest,
  SortField,
  TabularDataResponse,
  ValueCell,
} from "../../../../../../api/types/svTypes.ts";
import { rebuildSortingFields } from "../../../../reporting/sv/components/builder/tabular/preview/rebuildSortingFields.ts";
import type { TabularRunState } from "../../../../reporting/sv/components/viewer/storage/runState.ts";
import useGridStateViewer from "../../../../reporting/sv/components/viewer/tabular/hooks/useGridStateViewer.ts";
import { useRequestConfigViewer } from "../../../../reporting/sv/components/viewer/tabular/hooks/useRequestConfigViewer.ts";
import PreviewComponent from "../../../../reporting/sv/components/viewer/tabular/PreviewComponent.tsx";
import { useBiCommonDataContext } from "../../../../reporting/sv/contexts/BiCommonDataContext.ts";
import { generateRandomString } from "../../../utils/generateRandomString.ts";
import type { DrillDownArgs } from "../DrillDownArgs.ts";
import SmartViewWidgetViewerWrapper from "../SmartViewWidgetViewerWrapper";

interface Props {
  runState: TabularRunState;
  runReport: <TBody extends RunTabularReportRequest, TResponse extends TabularDataResponse>(
    dataSetId: string,
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  onDrillDown: (args: DrillDownArgs) => void;
  onSortingUpdate?: (newSorting: SortField[]) => void;
}

const SmartViewTabularWidgetViewer = ({ runState, runReport, onDrillDown, onSortingUpdate }: Props) => {
  const { reportId } = useBiCommonDataContext();
  const [etag, setEtag] = useState<string | undefined>(undefined);
  const [sessionId] = useState<string>(generateRandomString);

  const { requestConfig: viewConfig } = useRequestConfigViewer({
    conditions: runState.conditions,
    fields: runState.fields,
    sorting: runState.sort,
    reportId,
    sessionId: sessionId,
    isQueryLoggingActive: false,
  });

  const gridState = useGridStateViewer({
    fields: runState.fields,
    sorting: runState.sort,
    requestConfig: viewConfig,
    setEtag: setEtag,
    setResponse: undefined,
    updateDevTools: undefined,
    runReport,
  });

  const handleDrillDown = (field: ReportField, cell: ValueCell) => {
    onDrillDown({ field, cell, etag, sessionId });
  };

  const handleSortModelChange = useCallback(
    (newSorting: GridSortModel) => {
      if (!onSortingUpdate) return;
      const sortFields = rebuildSortingFields(newSorting, runState.fields, runState.sort);
      onSortingUpdate(sortFields);
    },
    [onSortingUpdate, runState.fields, runState.sort]
  );

  return (
    <SmartViewWidgetViewerWrapper initialized={gridState.initialized} validity={gridState.configurationValidity}>
      <PreviewComponent
        gridState={gridState}
        //Disabled due to view mode
        disableColumnManagement={true}
        fields={runState.fields}
        groups={runState.grouping}
        previewMode={"maximize"}
        settings={runState.settings}
        sorts={runState.sort}
        disableCellClick={true}
        onDrillDown={handleDrillDown}
        onColumnsSortingChanged={onSortingUpdate ? handleSortModelChange : undefined}
        onPredefinedCustomFieldType={undefined}
      />
    </SmartViewWidgetViewerWrapper>
  );
};

export default SmartViewTabularWidgetViewer;
